<template>
  <div class="picker">
    <div :class="{ loading: isLoading }">
      <span class="icon">&#x1f4cd;</span>
      <input v-model="query" type="text" :placeholder="placeholder" @blur="handleBlur" @click="init" @focus="init" @input="search" @keyup.up="moveUp" @keyup.down="moveDown" @keyup.enter="update(selected)" />
    </div>
    <ul v-show="isSearching">
      <li v-for="(pl, index) in data" :key="index" :class="{ selected: pl.id == selected.id }" @mouseout="focus(-1)" @mouseover="focus(index)">
        {{ pl.name_full }}
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, inject } from "vue";

// Props
const props = defineProps({
  place: [Object, String],
  placeholder: String,
});

// Emit
const emit = defineEmits(["error", "update:place"]);

// Inject dependencies
const axios = inject("axios");

// Local state
const axiosCancelTokenSource = ref(null);
const data = ref([]);
const focusIndex = ref(-1);
const isLoading = ref(false);
const isSearching = ref(false);
const query = ref(props.place.name === "" ? "" : props.place.name);
const querySaved = ref("");
const selected = ref(props.place ? props.place : null);

// Methods
const handleBlur = () => {
  if (focusIndex.value > -1) {
    update(data.value[focusIndex.value]);
  } else {
    update(selected.value);
  }
};

const focus = index => {
  focusIndex.value = index;
};

const init = () => {
  isSearching.value = true;
  query.value = querySaved.value;
};

const moveDown = () => {
  const last = data.value.length - 1;
  for (let i = 0; i <= last; i++) {
    if (data.value[i].id == selected.value.id) {
      if (i < last) {
        selected.value = data.value[i + 1];
      } else {
        selected.value = data.value[0];
      }
      break;
    }
  }
};

const moveUp = () => {
  const last = data.value.length - 1;
  for (let i = 0; i <= last; i++) {
    if (data.value[i].id == selected.value.id) {
      if (i > 0) {
        selected.value = data.value[i - 1];
      } else {
        selected.value = data.value[last];
      }
      break;
    }
  }
};

const scroll = () => {
  const el = document.querySelector(".picker");
  const rect = el.getBoundingClientRect();
  if (rect.top + 6 * 16 > (window.innerHeight || document.documentElement.clientHeight)) {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

const search = () => {
  if (query.value.length <= 1) return;
  isLoading.value = true;

  if (axiosCancelTokenSource.value != null) {
    axiosCancelTokenSource.value.cancel();
  }

  axiosCancelTokenSource.value = axios.CancelToken.source();

  axios
    .get(`${import.meta.env.VITE_APP_FLIGHTFOX_API_URL}/geographies?query=${query.value}`, {
      cancelToken: axiosCancelTokenSource.value.token,
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then(response => {
      data.value = response.data;
      selected.value = data.value[0];
      scroll();
      isLoading.value = false;
    })
    .catch(error => {
      isLoading.value = false;

      if (!axios.isCancel(error)) {
        emit("error", error);
      }
    });
};

const update = place => {
  if (place) {
    querySaved.value = query.value;
    query.value = place.name;
    selected.value = place;
    emit("update:place", place);
  }
  isSearching.value = false;
};
</script>
